import { css, cx } from "@emotion/css";
import debounce from "lodash/debounce";
import { useEffect, useRef, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { useTranslation } from "react-i18next";
import type { PDFDocumentProxy } from "pdfjs-dist";

import { configurePdfjsWorker } from "./configurePdfjsWorker";

type Props = {
  src: string;
  title: string;
  testID?: string;
  onLoad?: () => void;
  onError?: () => void;
  className?: string;
};

export function PdfCanvasPreview({
  src,
  title,
  testID = "pdf-canvas-preview",
  onLoad,
  onError,
  className,
}: Props) {
  const { t } = useTranslation();
  const pageLabel = (pageNumber: number) =>
    t("{{title}} page {{pageNumber}}", { title, pageNumber });
  const pageLabelRef = useRef(pageLabel);
  pageLabelRef.current = pageLabel;
  const containerRef = useRef<HTMLDivElement>(null);
  const [accessibleText, setAccessibleText] = useState("");
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  onLoadRef.current = onLoad;
  onErrorRef.current = onError;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let isCancelled = false;
    let pdfDocument: PDFDocumentProxy | null = null;
    let renderGeneration = 0;
    let lastRenderedWidth = 0;

    const renderPages = async (doc: PDFDocumentProxy) => {
      const width = container.clientWidth;
      if (width <= 0) {
        return;
      }

      const generation = ++renderGeneration;
      lastRenderedWidth = width;

      // Draw into a fragment and swap once. Clearing the container first
      // collapses overflow, and ContractPdfScroll would treat that as
      // "already scrolled" and unlock the accept checkbox.
      const fragment = document.createDocumentFragment();
      const outputScale = window.devicePixelRatio || 1;
      const accessibleText: string[] = [];

      for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
        const page = await doc.getPage(pageNumber);
        if (isCancelled || generation !== renderGeneration) {
          return;
        }

        const textContent = await page.getTextContent();
        if (isCancelled || generation !== renderGeneration) {
          return;
        }
        const pageText = textContent.items
          .map((item) => ("str" in item ? item.str : ""))
          .join(" ")
          .trim();
        if (pageText) {
          accessibleText.push(pageText);
        }

        const unscaled = page.getViewport({ scale: 1 });
        const scale = width / unscaled.width;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.className = styles.page;
        canvas.setAttribute("aria-label", pageLabelRef.current(pageNumber));

        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Canvas 2d context unavailable");
        }
        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
        await page.render({ canvasContext: context, viewport }).promise;
        if (isCancelled || generation !== renderGeneration) {
          return;
        }
        fragment.appendChild(canvas);
      }

      if (generation !== renderGeneration) {
        return;
      }

      container.replaceChildren(fragment);

      setAccessibleText(accessibleText.join("\n\n"));
      onLoadRef.current?.();
    };

    const load = async () => {
      try {
        await configurePdfjsWorker();
        const pdfjs = await import("pdfjs-dist");
        const loadingTask = pdfjs.getDocument({ url: src });
        pdfDocument = await loadingTask.promise;
        if (isCancelled) {
          await pdfDocument.destroy();
          return;
        }
        await renderPages(pdfDocument);
      } catch {
        if (!isCancelled) {
          onErrorRef.current?.();
        }
      }
    };

    load();

    const debouncedRerender = debounce(() => {
      if (!pdfDocument || isCancelled) {
        return;
      }
      const width = container.clientWidth;
      if (width <= 0 || width === lastRenderedWidth) {
        return;
      }
      renderPages(pdfDocument).catch(() => {
        if (!isCancelled) {
          onErrorRef.current?.();
        }
      });
    }, 200);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const { width } = entry.contentRect;
      if (width > 0 && width !== lastRenderedWidth) {
        debouncedRerender();
      }
    });
    observer.observe(container);

    return () => {
      isCancelled = true;
      renderGeneration += 1;
      observer.disconnect();
      debouncedRerender.cancel();
      pdfDocument?.destroy();
      container.replaceChildren();
      setAccessibleText("");
    };
  }, [src, title]);

  return (
    <div
      className={cx(styles.container, className)}
      data-testid={testID}
      role="document"
      aria-label={title}
    >
      {accessibleText ? (
        <VisuallyHidden>{accessibleText}</VisuallyHidden>
      ) : null}
      <div ref={containerRef} className={styles.pages} />
    </div>
  );
}

const styles = {
  container: css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  pages: css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  page: css({
    width: "100%",
    height: "auto",
    display: "block",
  }),
};
